package com.anjuros.website.redesign.components

import androidx.compose.runtime.Composable
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.styles.RdArrowStyle
import com.anjuros.website.redesign.styles.RdEyebrowStyle
import com.anjuros.website.redesign.styles.RdGhostButtonStyle
import com.anjuros.website.redesign.styles.RdH2Style
import com.anjuros.website.redesign.styles.RdLedeStyle
import com.anjuros.website.redesign.styles.RdPrimaryButtonStyle
import com.anjuros.website.redesign.styles.RdSectionHeadStyle
import com.anjuros.website.redesign.styles.RdSectionStyle
import com.anjuros.website.redesign.styles.RdWrapStyle
import com.anjuros.website.redesign.util.RdImage
import com.anjuros.website.redesign.util.RdRoute
import com.anjuros.website.redesign.util.RdText
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.navigation.Anchor
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.ElementScope
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

/** A full-width page section with the standard vertical padding and a centred content column. */
@Composable
fun RdSection(
    id: String? = null,
    labelledBy: String? = null,
    modifier: Modifier = RdSectionStyle.toModifier(),
    wrapModifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    Section(
        modifier
            .thenIf(id != null) { Modifier.id(id!!) }
            .thenIf(labelledBy != null) { Modifier.attrsModifier { attr("aria-labelledby", labelledBy!!) } }
            .toAttrs()
    ) {
        Div(RdWrapStyle.toModifier().then(wrapModifier).toAttrs()) { content() }
    }
}

@Composable
fun RdEyebrow(text: RdText) {
    P(RdEyebrowStyle.toModifier().toAttrs()) { Text(LocalText(text)) }
}

@Composable
fun RdLede(text: RdText) {
    P(RdLedeStyle.toModifier().toAttrs()) { Text(LocalText(text)) }
}

/** Eyebrow, H2 and optional lede at the top of a section. */
@Composable
fun RdSectionHead(eyebrow: RdText, headingId: String, heading: RdText, lede: RdText? = null) {
    Div(RdSectionHeadStyle.toModifier().toAttrs()) {
        RdEyebrow(eyebrow)
        H2(RdH2Style.toModifier().id(headingId).toAttrs()) { Text(LocalText(heading)) }
        if (lede != null) RdLede(lede)
    }
}

/** An unstyled link with client-side routing; all its styling comes from [modifier]. */
@Composable
fun RdLink(path: String, modifier: Modifier = Modifier, content: @Composable () -> Unit) {
    Anchor(path, modifier.toAttrs()) { content() }
}

/**
 * A link to the home page. When already on the home page, a plain click scrolls back to the top
 * instead of routing; from any other page it navigates home as usual.
 */
@Composable
fun RdHomeLink(modifier: Modifier = Modifier, content: @Composable () -> Unit) {
    RdLink(
        RdRoute.HOME,
        modifier.onClick { evt ->
            val plainClick = evt.button == 0.toShort() && !evt.ctrlKey && !evt.metaKey && !evt.shiftKey && !evt.altKey
            if (!plainClick || window.location.pathname != RdRoute.HOME) return@onClick
            // Runs before the router's own click listener, so stop it from navigating.
            evt.preventDefault()
            evt.nativeEvent.stopImmediatePropagation()
            val options: dynamic = js("({})")
            options.top = 0
            options.behavior = if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) "auto" else "smooth"
            window.scrollTo(options.unsafeCast<org.w3c.dom.ScrollToOptions>())
            if (window.location.hash.isNotEmpty()) window.history.replaceState(null, "", RdRoute.HOME)
        },
        content
    )
}

/** A link to another website, opened in a new tab. */
@Composable
fun RdExternalLink(url: String, modifier: Modifier = Modifier, content: @Composable () -> Unit) {
    A(url, modifier.toAttrs { attr("target", "_blank"); attr("rel", "noopener") }) { content() }
}

@Composable
fun RdPrimaryButton(path: String, text: RdText, withArrow: Boolean = false, modifier: Modifier = Modifier) {
    RdLink(path, RdPrimaryButtonStyle.toModifier().then(modifier)) { ButtonLabel(text, withArrow) }
}

@Composable
fun RdGhostButton(path: String, text: RdText, withArrow: Boolean = false, modifier: Modifier = RdGhostButtonStyle.toModifier()) {
    RdLink(path, modifier) { ButtonLabel(text, withArrow) }
}

@Composable
private fun ButtonLabel(text: RdText, withArrow: Boolean) {
    Text(LocalText(text))
    if (withArrow) {
        Span(RdArrowStyle.toModifier().attrsModifier { attr("aria-hidden", "true") }.toAttrs()) { Text("→") }
    }
}

/** An image with its intrinsic size set, so the page doesn't jump while it loads. */
@Composable
fun RdPicture(asset: RdImage.Asset, alt: String, lazy: Boolean = true, modifier: Modifier = Modifier) {
    Image(
        src = asset.src,
        alt = alt,
        modifier = modifier.attrsModifier {
            attr("width", asset.width.toString())
            attr("height", asset.height.toString())
            if (lazy) attr("loading", "lazy")
        }
    )
}

/** An HTML tag Compose HTML has no function for, such as figure, figcaption, details or summary. */
@Composable
fun RdTag(
    name: String,
    modifier: Modifier = Modifier,
    content: (@Composable ElementScope<HTMLElement>.() -> Unit)? = null,
) {
    TagElement<HTMLElement>(name, modifier.toAttrs(), content)
}

/** Adds or replaces a JSON-LD block in the document head so search engines can read it. */
fun setJsonLd(id: String, json: String) {
    val script = document.getElementById(id) ?: document.createElement("script").also {
        it.id = id
        it.setAttribute("type", "application/ld+json")
        document.head?.appendChild(it)
    }
    script.textContent = json
}

/** Quotes and escapes a value for use inside hand-written JSON. */
fun jsonString(value: String): String = "\"" + value.replace("\\", "\\\\").replace("\"", "\\\"") + "\""

/**
 * Scrolls to the element with [id] after the next render, for in-page links whose target only
 * appears once something else changes (e.g. a pill that first clears a search filter).
 */
fun rdScrollToAfterRender(id: String) {
    // Two frames: the first lets Compose apply the change, the second runs after layout.
    window.requestAnimationFrame {
        window.requestAnimationFrame {
            val target = document.getElementById(id) ?: return@requestAnimationFrame
            val options: dynamic = js("({})")
            options.behavior = if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) "auto" else "smooth"
            options.block = "start"
            target.asDynamic().scrollIntoView(options)
            window.history.replaceState(null, "", "#$id")
        }
    }
}
